// The deal's counterparty: a combo box over the counterparty register.
//
// Typing searches the register and lists what matches; picking a match links the deal to that
// record by its identifier. A name that matches nothing is kept as typed and the deal is not
// linked - business rule 4 and TX-US-0004 AC6: someone entering a deal should not have to open a
// counterparty file before writing anything down. The field always says which of the two it is,
// and the deal stores it, so unlinked names can be reviewed later. A counterparty is not created
// from here (TX-US-0031): that is done in the counterparty register, where its onboarding
// lifecycle starts.
//
// `category` narrows the matches to one kind of counterparty (TX-US-0016 AC7). A deal passes none
// (TX-US-0039): its one counterparty is a supplier or a customer by the deal's direction, and
// every counterparty is offered whatever its type.

import { useId, useState } from 'react';

import { pickerMark } from '../../counterparties/labels.js';
import CounterpartySuggestions from './CounterpartySuggestions.jsx';
import useCounterpartySearch from './useCounterpartySearch.js';

export default function CounterpartySelect({
  label, category, name, linked, resolved, onChange, error, flag = null,
}) {
  const listId = useId();
  // Open while a name is being typed; closed once one is settled, so a chosen counterparty does
  // not sit under a list suggesting the thing already chosen.
  const [picking, setPicking] = useState(false);
  const [active, setActive] = useState(-1);
  // The record that was picked, held here so the hint below speaks about it whether or not the
  // form's own copy of the register holds it.
  const [chosen, setChosen] = useState(null);
  const { matches, searching } = useCounterpartySearch(picking ? name : '', category);

  // What this field is currently pointing at, whichever way it got there.
  const record = chosen ?? resolved;
  const typed = (name ?? '').trim();

  const choose = (counterparty) => {
    setPicking(false);
    setActive(-1);
    setChosen(counterparty);
    onChange(counterparty.name, counterparty);
  };

  const onKeyDown = (event) => {
    if (!picking || matches.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % matches.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index <= 0 ? matches.length - 1 : index - 1));
    } else if (event.key === 'Enter' && active >= 0) {
      // Enter on a highlighted match picks it; without one it stays the form's save.
      event.preventDefault();
      choose(matches[active]);
    } else if (event.key === 'Escape') {
      setPicking(false);
      setActive(-1);
    }
  };

  return (
    <label className="field counterparty-select">
      <span className="field-label">{label}{flag}</span>
      <span className="field-control">
        <input
          role="combobox"
          aria-expanded={picking && matches.length > 0}
          aria-controls={listId}
          aria-autocomplete="list"
          value={name ?? ''}
          onChange={(event) => {
            setPicking(true);
            setActive(-1);
            // Typing over a selection makes it a name again, and the parent decides whether that
            // name happens to match a record.
            setChosen(null);
            onChange(event.target.value);
          }}
          onKeyDown={onKeyDown}
          // Leaving the field settles the name as it stands. A click on a match does not get
          // here first: the list keeps its mousedown from moving focus.
          onBlur={() => {
            setPicking(false);
            setActive(-1);
          }}
          placeholder="Type to search the counterparty register"
          autoComplete="off"
        />
      </span>

      {picking && (
        <CounterpartySuggestions id={listId} matches={matches} active={active} onPick={choose} />
      )}

      {/* Linked or not, said in words: it is the difference between a deal that points at a
          counterparty record and one that carries a name somebody typed. Not said while a search
          is still in flight - "not on the register" must not be claimed before the answer. */}
      {typed && linked && (
        <span className="link-state linked">
          Linked to the counterparty register
          {pickerMark(record?.status) && ` — ${pickerMark(record.status).clause}`}
        </span>
      )}
      {typed && !linked && !searching && (
        <span className="link-state unlinked">
          {picking && matches.length > 0
            ? 'Pick a match to link it, or keep the name as typed'
            : 'Not on the register — kept as typed, not linked'}
        </span>
      )}

      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
